import React from 'react';
import { Star } from 'lucide-react';

// Official multi-colour Google "G" mark.
const GoogleG = ({ className = 'w-4 h-4' }) => (
  <svg className={className} viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

// A clickable, on-brand Google rating badge that links out to the live Google
// reviews page. Renders nothing unless an owner-set review URL exists.
export const GoogleReviewsBadge = ({ settings, className = '', testId = 'google-reviews-badge' }) => {
  const url = settings?.google_reviews_url;
  if (!url) return null;

  const rating = (settings?.google_reviews_rating ?? '').toString().trim();
  const count = (settings?.google_reviews_count ?? '').toString().trim();
  const numeric = parseFloat(rating) || 0;

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer nofollow"
      data-testid={testId}
      aria-label={`Read our Google reviews${rating ? ` — rated ${rating} out of 5` : ''}${count ? ` from ${count} reviews` : ''} (opens in a new tab)`}
      className={`group inline-flex items-center gap-2.5 rounded-full border border-shrimp-border/70 bg-white/95 px-4 py-2 shadow-sm backdrop-blur transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 hover:border-shrimp-green/60 ${className}`}
    >
      <GoogleG className="w-4 h-4 shrink-0" />
      {rating && <span className="text-sm font-semibold leading-none text-shrimp-dark">{rating}</span>}
      <span className="flex items-center gap-0.5" aria-hidden="true">
        {[0, 1, 2, 3, 4].map((i) => {
          const fill = Math.max(0, Math.min(1, numeric - i));
          return (
            <span key={i} className="relative inline-block h-4 w-4">
              <Star className="absolute inset-0 h-4 w-4 text-amber-300" />
              <span className="absolute inset-0 overflow-hidden" style={{ width: `${fill * 100}%` }}>
                <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
              </span>
            </span>
          );
        })}
      </span>
      <span className="whitespace-nowrap text-xs font-medium leading-none text-shrimp-text transition-colors group-hover:text-shrimp-green">
        {count ? `${count} Google reviews` : 'Read our Google reviews'}
      </span>
    </a>
  );
};

export default GoogleReviewsBadge;
